import { createRecentSearchStore } from './component-search-shared.js';

(() => {
  class HeaderPredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.resultsContainer = null;
      this.statusElement = null;
      this.headerRoot = null;
      this.input = null;
      this.form = null;
      this.clearButton = null;
      this.emptyStateTemplate = null;
      this.recentSearchesMax = 4;
      this.recentStore = createRecentSearchStore({ maxItems: this.recentSearchesMax });

      this.debounceTimer = 0;
      this.fetchController = null;
      this.requestToken = 0;
      this.activeIndex = -1;
      this.isOpen = false;

      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFormReset = this.onFormReset.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onHeaderSearchState = this.onHeaderSearchState.bind(this);
      this.onResultsClick = this.onResultsClick.bind(this);
    }

    connectedCallback() {
      // Support both the desktop header panel and the mobile search drawer.
      // The drawer uses [data-search-drawer] as its root so the element can
      // find its sibling form/input without being inside .header.
      this.headerRoot =
        this.closest('.header[data-section-type="header"]') ||
        this.closest('[data-search-drawer]');

      this.resultsContainer = this.querySelector('[data-predictive-search]');
      this.statusElement = this.querySelector('[data-predictive-search-status]');
      this.emptyStateTemplate = this.querySelector('[data-predictive-empty-state-template]');

      if (!this.headerRoot || !this.resultsContainer) return;

      this.input = this.headerRoot.querySelector('[data-header-search-input]');
      this.form = this.headerRoot.querySelector('[data-header-search-form]');
      this.clearButton = this.headerRoot.querySelector('[data-header-search-clear]');

      if (!this.input || !this.form) return;

      if (this.emptyStateTemplate) {
        const maxItems = Number.parseInt(this.emptyStateTemplate.dataset.recentMax || '4', 10);
        if (Number.isFinite(maxItems) && maxItems > 0) {
          this.recentSearchesMax = maxItems;
          this.recentStore.setMaxItems(this.recentSearchesMax);
        }
      }

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('keydown', this.onKeydown);
      this.form.addEventListener('reset', this.onFormReset);
      this.form.addEventListener('submit', this.onFormSubmit);
      this.resultsContainer.addEventListener('click', this.onResultsClick);
      document.addEventListener('click', this.onDocumentClick);
      this.headerRoot.addEventListener('theme:header-search:state', this.onHeaderSearchState);
      this.syncClearButton();
    }

    disconnectedCallback() {
      window.clearTimeout(this.debounceTimer);
      this.abortRequest();

      if (this.input) {
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('focus', this.onFocus);
        this.input.removeEventListener('keydown', this.onKeydown);
      }

      if (this.form) {
        this.form.removeEventListener('reset', this.onFormReset);
        this.form.removeEventListener('submit', this.onFormSubmit);
      }

      if (this.resultsContainer) {
        this.resultsContainer.removeEventListener('click', this.onResultsClick);
      }

      if (this.headerRoot) {
        this.headerRoot.removeEventListener('theme:header-search:state', this.onHeaderSearchState);
      }

      document.removeEventListener('click', this.onDocumentClick);
    }

    get endpoint() {
      return this.dataset.endpoint || '/search/suggest';
    }

    get sectionRenderId() {
      return this.dataset.sectionRenderId || 'predictive-search';
    }

    get resourcesType() {
      return this.dataset.resourcesType || 'product,query,collection,page,article';
    }

    get resourcesLimit() {
      return this.dataset.resourcesLimit || '6';
    }

    get errorText() {
      return this.dataset.errorText || 'Something went wrong. Please try again.';
    }

    get query() {
      return this.input ? this.input.value.trim() : '';
    }

    onHeaderSearchState(event) {
      if (!event || !event.detail) return;
      if (event.detail.open === false) {
        this.closeResults();
        return;
      }

      if (event.detail.open === true && !this.query.length) {
        this.renderEmptyState();
      }
    }

    onInput() {
      this.activeIndex = -1;
      this.syncActiveDescendant();
      this.syncClearButton();

      if (!this.query.length) {
        this.renderEmptyState();
        return;
      }

      this.fetchWithDebounce(this.query);
    }

    onFocus() {
      if (!this.query.length) {
        this.renderEmptyState();
        return;
      }

      if (this.isOpen && this.resultsContainer && this.resultsContainer.children.length > 0) {
        this.showResults();
        return;
      }

      this.fetchWithDebounce(this.query);
    }

    onFormReset() {
      this.abortRequest();
      this.setStatusText('');
      this.syncClearButton();
      this.renderEmptyState();
    }

    onFormSubmit() {
      if (this.query.length) {
        this.recentStore.push(this.query);
      }
      this.closeResults();
    }

    onDocumentClick(event) {
      if (!event || !event.target) return;
      if (this.contains(event.target) || (this.headerRoot && this.headerRoot.contains(event.target))) return;
      this.closeResults();
    }

    onResultsClick(event) {
      if (!event || !event.target) return;

      const clearButton = event.target.closest('[data-recent-searches-clear]');
      if (clearButton) {
        event.preventDefault();
        this.clearRecentSearches();
        this.renderRecentSearches();
        return;
      }

      const termButton = event.target.closest('[data-search-term]');
      if (termButton) {
        const term = (termButton.dataset.searchTerm || '').trim();
        if (!term || !this.input) return;

        event.preventDefault();
        this.input.value = term;
        this.recentStore.push(term);
        this.syncClearButton();
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus({ preventScroll: true });
        return;
      }

      const link = event.target.closest('a');
      if (link && this.query.length) {
        this.recentStore.push(this.query);
      }
    }

    onKeydown(event) {
      const key = event.key;
      const options = this.getOptions();

      if (key === 'Escape') {
        if (!this.isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeResults();
        return;
      }

      if (!options.length) return;

      if (key === 'ArrowDown') {
        event.preventDefault();
        this.moveSelection(1, options);
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        this.moveSelection(-1, options);
        return;
      }

      if (key === 'Tab') {
        if (!this.isOpen) return;
        event.preventDefault();
        this.moveSelection(event.shiftKey ? -1 : 1, options);
        return;
      }

      if (key === 'Enter' && this.activeIndex >= 0) {
        const activeOption = options[this.activeIndex];
        const actionTarget = activeOption.querySelector('a,button');
        if (!actionTarget) return;
        event.preventDefault();
        actionTarget.click();
      }
    }

    fetchWithDebounce(term) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => {
        this.getSearchResults(term);
      }, 220);
    }

    abortRequest() {
      if (!this.fetchController) return;
      this.fetchController.abort();
      this.fetchController = null;
    }

    async getSearchResults(term) {
      if (!term) {
        this.renderEmptyState();
        return;
      }

      this.abortRequest();
      this.fetchController = new AbortController();
      const currentToken = ++this.requestToken;
      this.setLoadingState(true);

      const requestUrl = new URL(this.endpoint, window.location.origin);
      requestUrl.searchParams.set('q', term);
      requestUrl.searchParams.set('section_id', this.sectionRenderId);
      requestUrl.searchParams.set('resources[type]', this.resourcesType);
      requestUrl.searchParams.set('resources[limit_scope]', 'each');
      requestUrl.searchParams.set('resources[limit]', this.resourcesLimit);
      requestUrl.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

      try {
        const response = await fetch(requestUrl.toString(), { signal: this.fetchController.signal });
        if (!response.ok) {
          throw new Error(String(response.status));
        }

        const html = await response.text();
        if (currentToken !== this.requestToken) return;

        this.renderResults(html);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        this.renderError();
      } finally {
        if (currentToken === this.requestToken) {
          this.setLoadingState(false);
        }
      }
    }

    renderResults(markupText) {
      if (!this.resultsContainer) return;

      const parsedDocument = new DOMParser().parseFromString(markupText, 'text/html');
      const sectionMarkup =
        parsedDocument.getElementById(`shopify-section-${this.sectionRenderId}`) ||
        parsedDocument.querySelector('[id^="shopify-section-"][id*="predictive-search"]');

      if (!sectionMarkup) {
        this.closeResults();
        return;
      }

      this.resultsContainer.innerHTML = sectionMarkup.innerHTML;
      this.activeIndex = -1;
      this.syncOptionSelection();
      this.syncActiveDescendant();
      this.setStatusFromResults();
      this.showResults();
    }

    renderEmptyState() {
      if (!this.resultsContainer) return;
      if (!this.emptyStateTemplate) {
        this.closeResults();
        return;
      }

      const markup = this.emptyStateTemplate.innerHTML.trim();
      if (!markup.length) {
        this.closeResults();
        return;
      }

      this.abortRequest();
      this.removeAttribute('loading');
      this.resultsContainer.innerHTML = markup;
      this.activeIndex = -1;
      this.syncOptionSelection();
      this.syncActiveDescendant();
      this.renderRecentSearches();
      this.setStatusText('');
      this.showResults();
    }

    setLoadingState(isLoading) {
      if (isLoading) {
        this.setAttribute('loading', 'true');
        const loadingText = this.statusElement ? this.statusElement.dataset.loadingText : '';
        this.setStatusText(loadingText || 'Loading...');
        return;
      }

      this.removeAttribute('loading');
    }

    syncClearButton() {
      if (!this.clearButton || !this.input) return;
      this.clearButton.hidden = this.input.value.length === 0;
    }

    setStatusText(text) {
      if (!this.statusElement) return;
      this.statusElement.textContent = text || '';
      this.statusElement.setAttribute('aria-hidden', text ? 'false' : 'true');
    }

    setStatusFromResults() {
      if (!this.resultsContainer) return;
      const statusValue = this.resultsContainer.querySelector('[data-predictive-search-live-region-count-value]');
      const text = statusValue ? statusValue.textContent.trim() : '';
      this.setStatusText(text);
    }

    showResults() {
      this.isOpen = true;
      this.setAttribute('open', 'true');
      if (this.headerRoot) {
        this.headerRoot.dispatchEvent(new CustomEvent('theme:predictive-search:show'));
      }
    }

    closeResults() {
      this.abortRequest();
      this.isOpen = false;
      this.removeAttribute('open');
      this.activeIndex = -1;
      this.syncOptionSelection();
      this.syncActiveDescendant();

      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
      }

      if (this.headerRoot) {
        this.headerRoot.dispatchEvent(new CustomEvent('theme:predictive-search:hide'));
      }
    }

    renderError() {
      if (!this.resultsContainer) return;
      this.resultsContainer.innerHTML = `<div class="predictive-search-results predictive-search-results--error"><div class="predictive-search-results__groups"><p class="predictive-search-results__empty-text">${this.escapeHtml(this.errorText)}</p></div></div>`;
      this.setStatusText(this.errorText);
      this.showResults();
    }

    clearRecentSearches() {
      this.recentStore.clear();
    }

    renderRecentSearches() {
      if (!this.resultsContainer) return;

      const recentGroup = this.resultsContainer.querySelector('[data-recent-searches-group]');
      const recentList = this.resultsContainer.querySelector('[data-recent-searches-list]');
      const clearRecentButton = this.resultsContainer.querySelector('[data-recent-searches-clear]');

      if (!recentGroup || !recentList) return;

      const recentTerms = this.recentStore.get();
      recentList.innerHTML = '';

      if (!recentTerms.length) {
        recentGroup.hidden = true;
        if (clearRecentButton) clearRecentButton.hidden = true;
        return;
      }

      const listFragment = document.createDocumentFragment();
      recentTerms.forEach((term) => {
        const listItem = document.createElement('li');
        listItem.className = 'predictive-search-empty__item';

        const button = document.createElement('button');
        button.className = 'predictive-search-empty__term predictive-search-empty__term--recent';
        button.type = 'button';
        button.dataset.searchTerm = term;
        button.textContent = term;

        listItem.appendChild(button);
        listFragment.appendChild(listItem);
      });

      recentList.appendChild(listFragment);
      recentGroup.hidden = false;
      if (clearRecentButton) clearRecentButton.hidden = false;
    }

    escapeHtml(input) {
      const text = String(input || '');
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    getOptions() {
      if (!this.resultsContainer) return [];
      return Array.from(this.resultsContainer.querySelectorAll('[role="option"]')).filter((item) => item.offsetParent !== null);
    }

    moveSelection(step, options) {
      if (!options.length) return;
      if (this.activeIndex < 0) {
        this.activeIndex = step > 0 ? 0 : options.length - 1;
      } else {
        this.activeIndex = (this.activeIndex + step + options.length) % options.length;
      }

      this.syncOptionSelection();
      this.syncActiveDescendant();

      const activeOption = options[this.activeIndex];
      if (activeOption) {
        activeOption.scrollIntoView({ block: 'nearest' });
      }
    }

    syncOptionSelection() {
      const options = this.getOptions();
      options.forEach((item, index) => {
        if (index === this.activeIndex) {
          item.setAttribute('aria-selected', 'true');
        } else {
          item.setAttribute('aria-selected', 'false');
        }
      });
    }

    syncActiveDescendant() {
      if (!this.input) return;
      const options = this.getOptions();
      const activeOption = this.activeIndex >= 0 ? options[this.activeIndex] : null;
      if (activeOption && activeOption.id) {
        this.input.setAttribute('aria-activedescendant', activeOption.id);
      } else {
        this.input.setAttribute('aria-activedescendant', '');
      }
    }
  }

  if (!customElements.get('header-predictive-search')) {
    customElements.define('header-predictive-search', HeaderPredictiveSearch);
  }
})();
